import { useEffect, useMemo, useRef, useState } from "react";
import MaterialTable, { MTableToolbar } from "material-table";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { jsPDF } from "jspdf";
import Base from "../../Base/views/Base";
import { useDispatch, useSelector } from "react-redux";
import {
  GetVisitorsAction,
  GetVMPreRegistrationVisitorAction,
  UpdateBulkVisitorsAction,
} from "../../../../store/Employee/Visitors/Action";
import LogoutIcon from "@mui/icons-material/Logout";
import ConfirmationDialog from "../../Base/views/ConfirmationDialog";
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { AddVisitorTokenConfigHelper, GetCanteenTeamSizeUtilizedHelper } from "../../CanteenManagement/helpers/TokenConfigHelper";
import { DateConvertInDbFormat, getUserData } from "../../Base/helper/baseFunctions";
import { generateShortUUID } from "../../CanteenManagement/views/TokenConfig";
import CanteenTeamSizeExceededWarningPopup from "../../CanteenManagement/helpers/TeamSizeValPopup";
import BackdropMeepl from "../../Ancillary/AnimationComponents/BackdropMeepl";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomToggle from "../../Base/views/CustomToggle";
import { getVMVisitorManagementTransactionsHelper } from "../helper/PreRegistrationHelper";
import { GetVMExtraSettingConfigHelper } from "../../VisitorSettings/helper/CheckInFormConfig/CheckInFormConfigHelper";

const VisitorsRecord = () => {
  const dispatch = useDispatch();
  const canteenTeamSizeRef = useRef();
  const { visitors_data, visitorLoading } = useSelector(
    (state) => state?.EmployeeReducer?.VisitorsReducer
  );
  const { open, fixedMenu, isMobile } = useSelector(
    (state) => state?.MenuReducer
  );
  const { atsModuleData } = useSelector(
    (state) => state?.RoleAndAccessReducer?.ATSModuleReducer
  );
  const CanteenAccess = atsModuleData?.some(
    (item) => item.module_name === "Canteen Management"
  );
  const { vmPreRegistrationData, vmPreRegistrationLoading } = useSelector(
    (state) => state?.EmployeeReducer?.VisitorsReducer
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [anchorE, setAnchorE] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentData, setCurrentData] = useState(null);
  const [anchorE2, setAnchorE2] = useState(null);
  const [mealData, setMealData] = useState(null);
  const [openDialog2, setOpenDialog2] = useState(false);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [transactionRows, setTransactionRows] = useState([]);
  const [transactionVisitor, setTransactionVisitor] = useState(null);
  const [transactionLoading, setTransactionLoading] = useState(null);
  const [canteenTeamSize, setCanteenTeamSize] = useState(null);
  const [isAll, setIsAll] = useState(true);
  const [isCanteenConfigEnabled, setIsCanteenConfigEnabled] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const allowMealAssigning = CanteenAccess && isCanteenConfigEnabled;
  const fetchCanteenTeamSize = () => {
    GetCanteenTeamSizeUtilizedHelper().then(res => {
      if (res?.data?.statuscode === 200) {
        const resData = res?.data?.data?.[0]
        setCanteenTeamSize(resData?.fn_get_canteen_team_size_utilized)
      }
    });
    GetVMExtraSettingConfigHelper().then(res => {
      if (res?.data?.statuscode === 200) {
        const resData = res?.data?.data?.[0]?.is_canteen_token_applicable;
        setIsCanteenConfigEnabled(!!resData);
      }
    });
  }
  useEffect(() => {
    if (isAll) {
      dispatch(GetVisitorsAction());
      fetchCanteenTeamSize();
    } else {
      dispatch(GetVMPreRegistrationVisitorAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAll]);
  const formatTime = (date) => {
    if (!date) return "N/A";
    const time = new Date(date);
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    return time.toLocaleString("en-US", {
      ...options,
      timeZone: "UTC",
    });
  };

  const handleExportTransactionsPDF = () => {
    if (!transactionRows || transactionRows.length === 0) return;
    setPdfGenerating(true);
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 40;
    let y = 50;
    pdf.setFontSize(14);
    const title = transactionVisitor
      ? `In-Out Details for ${transactionVisitor?.visitors_name}`
      : "In-Out Details";
    pdf.text(title, margin, y);
    y += 20;

    // Table columns: #, Name, Type, Timestamp
    pdf.setFontSize(11);
    pdf.setFont("helvetica", "bold");
    const colWidths = [30, 220, 70, pageWidth - margin * 2 - (30 + 220 + 70)];
    const headers = ["#", "Name", "Type", "Timestamp"];
    let x = margin;
    headers.forEach((h, i) => {
      pdf.text(h, x + 4, y);
      x += colWidths[i];
    });
    y += 16;
    pdf.setDrawColor(200);
    pdf.line(margin, y - 8, pageWidth - margin, y - 8);
    pdf.setFont("helvetica", "normal");

    const lineHeight = 14;

    transactionRows.forEach((t, idx) => {
      const name = t?.visitors_name || "-";
      const type = t?.type || "-";
      const timestamp = t?.checkin_timestamp
        ? formatTime(t.checkin_timestamp)
        : t?.checkout_timestamp
        ? formatTime(t.checkout_timestamp)
        : t?.timestamp
        ? formatTime(t.timestamp)
        : "-";

      const nameLines = pdf.splitTextToSize(name, colWidths[1] - 8);
      const timestampLines = pdf.splitTextToSize(timestamp, colWidths[3] - 8);
      const maxLines = Math.max(nameLines.length, timestampLines.length, 1);
      const rowHeight = lineHeight * maxLines + 6;

      if (y + rowHeight > pdf.internal.pageSize.getHeight() - 40) {
        pdf.addPage();
        y = 50;
      }

      x = margin;
      pdf.text(String(idx + 1), x + 4, y + 12);
      x += colWidths[0];

      pdf.text(nameLines, x + 4, y + 12);
      x += colWidths[1];

      pdf.text(type, x + 4, y + 12);
      x += colWidths[2];

      pdf.text(timestampLines, x + 4, y + 12);

      y += rowHeight;
    });

    const fileName = `${(transactionVisitor?.visitors_name || "details").replace(/\s+/g, "_")}-in-out.pdf`;
    pdf.save(fileName);
    setTimeout(() => {    
      setPdfGenerating(false);
    }, 1000);
  };
  const formattedVisitorsData = useMemo(
    () =>
      visitors_data?.length > 0
        ? visitors_data.map((item) => ({
          ...item,
          checkin_timestamp: formatTime(item?.checkin_timestamp),
          checkout_timestamp: formatTime(item?.checkout_timestamp),
          id_proof: item?.id_proof || "N/A",
          vehicle_no: item?.vehicle_no || "N/A",
        }))
        : [],
    [visitors_data]
  );
  const getLocalISOTime = () => {
    const now = new Date();
    const offsetInMinutes = now.getTimezoneOffset();
    const localTime = new Date(now.getTime() - offsetInMinutes * 60000);
    return localTime.toISOString().slice(0, -1);
  };
  const handleCheckOutConfirmation = (rowData) => {
    setCurrentData(rowData);
    setAnchorE(null);
    setOpenDialog(true);
  };
  const handleMealConfirmation = (rowData) => {
    if (canteenTeamSizeRef.current) {
      canteenTeamSizeRef.current.validateAIConfig(canteenTeamSize).then(res => {
        if (res.ok) {
          setMealData(rowData);
          setAnchorE2(null);
          setOpenDialog2(true);
        }
      })
    };
  };
  const handleShowTransactions = async (rowData) => {
    setTransactionVisitor(rowData);
    setTransactionLoading(rowData?.visitors_management_pk);
    try {
      const res = await getVMVisitorManagementTransactionsHelper({visitors_management_pk: rowData?.visitors_management_pk});
      if (res?.data?.statuscode === 200) {
        const resData = res?.data?.data || [];
        setTransactionRows(resData);
      }
      setTransactionDialogOpen(true);
    } catch (error) {
      setTransactionRows([]);
      setTransactionDialogOpen(true);
    } finally {
      setTransactionLoading(null);
    }
  };

  const handleMealAssigned = () => {
    const companyName = getUserData().company_full_name?.toUpperCase() || ""
    AddVisitorTokenConfigHelper({
      uuid: generateShortUUID(companyName),
      record_date: DateConvertInDbFormat(),
      canteen_status: true,
      visitors_management_pk: mealData?.visitors_management_pk,
    }).then((res => {
      if (res?.data?.statuscode === 200) {
        setMealData(null);
        setOpenDialog2(false);
        dispatch(GetVisitorsAction());
        fetchCanteenTeamSize();
      }
    }))
  };
  const handleCheckout = () => {
    const checkout_timestamp = getLocalISOTime();
    dispatch(
      UpdateBulkVisitorsAction({
        visitors_management_pk: `{${currentData?.visitors_management_pk}}`,
        checkout_timestamp,
      })
    );
    setCurrentData(null);
    setOpenDialog(false);
  };
  const handleCheckoutAll = () => {
    const visitors_management_pk = selectedRows?.map(
      (item) => item.visitors_management_pk
    );
    const checkout_timestamp = getLocalISOTime();
    dispatch(
      UpdateBulkVisitorsAction({
        visitors_management_pk: `{${visitors_management_pk}}`,
        checkout_timestamp,
      })
    );
    setSelectedRows([]);
    setOpenDialog(false);
  };
  const handleSelect = (rows) => {
    setSelectedRows(rows);
  };
  const handleToggle = (e) => {
    setIsAll(!isAll);
  };

  return (
    <Base>
      <Box sx={{ height: "calc(100vh - 75px)", overflowY: "auto", position: "relative" }}>
        <Box
          width={
            isMobile
              ? "100%"
              : !fixedMenu && !open
                ? "calc(100vw - 75px)"
                : "calc(100vw - 255px)"
          }
        >
          <MaterialTable
            isLoading={visitorLoading || vmPreRegistrationLoading}
            columns={isAll ?
              [
                {
                  field: "region",
                  title: "Entry Point",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 150,
                    minWidth: 150,
                    padding: "5px 0px 5px 5px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 150,
                    minWidth: 150,
                    textAlign: "start",
                    padding: "5px 0px 5px 5px",
                  },
                },
                {
                  field: "visitors_name",
                  title: "Visitor Name",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 125,
                    minWidth: 125,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 125,
                    minWidth: 125,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                },
                {
                  field: "employee_name",
                  title: "Host",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 145,
                    minWidth: 145,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 145,
                    minWidth: 145,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                },
                {
                  field: "reason",
                  title: "Purpose",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 135,
                    minWidth: 135,
                    padding: "5px 0px 5px 5px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 135,
                    minWidth: 135,
                    textAlign: "start",
                    padding: "5px 0px 5px 7px",
                  },
                },
                {
                  field: "checkin_timestamp",
                  title: "Check In",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 170,
                    minWidth: 170,
                    padding: "5px 0px 5px 5px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 170,
                    minWidth: 170,
                    textAlign: "start",
                    padding: "5px 0px 5px 7px",
                  },
                },
                {
                  field: "checkout_timestamp",
                  title: "Check Out",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 175,
                    minWidth: 175,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 175,
                    minWidth: 175,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                  render: (rowData) =>
                    rowData.checkout_timestamp === "N/A" ? (
                      <IconButton
                        color="primary"
                        onClick={() => handleCheckOutConfirmation(rowData)}
                      >
                        <LogoutIcon />
                      </IconButton>
                    ) : (
                      rowData.checkout_timestamp
                    ),
                },
                {
                  field: "in_out_transaction",
                  title: "In-Out Detail",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 50,
                    minWidth: 50,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 50,
                    minWidth: 50,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                  render: (rowData) => (
                    <Tooltip title="View In-Out Detail">
                      <IconButton
                        color="primary"
                        onClick={() => handleShowTransactions(rowData)}
                        disabled={transactionLoading}
                      >
                        {rowData?.visitors_management_pk === transactionLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <InfoOutlinedIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  ),
                },
                {
                  field: "canteen_uuid",
                  title: (<span>
                    Assign Meal&nbsp;
                    <Tooltip title="Organization Canteen Capacity: Indicates the unallocated portion of the organization’s canteen token capacity, applicable to both employees and visitors." placement="bottom" arrow>
                      <Badge
                        badgeContent={canteenTeamSize || "0"}
                        color="secondary"
                        overlap="circular"
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "0.6rem",
                            height: 14,
                            minWidth: 14,
                            top: 3,
                            right: -8,
                            padding: "0 3px",
                          },
                          cursor: "pointer",
                        }}
                      >
                        <InfoOutlinedIcon fontSize="small" color="action" style={{ verticalAlign: "middle", cursor: "pointer", marginBottom: "4px" }} />
                      </Badge>
                    </Tooltip>
                  </span>),
                  editable: "never",
                  headerStyle: {
                    maxWidth: 175,
                    minWidth: 175,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 175,
                    minWidth: 175,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                  render: (rowData) =>
                    rowData.canteen_uuid ? rowData.canteen_uuid : rowData.checkout_timestamp === "N/A" ? (
                      <Tooltip title="Assign Meal Token">
                        <span>
                          <IconButton
                            color="primary"
                            onClick={() => handleMealConfirmation(rowData)}
                          >
                            <RestaurantIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      "No Meal"
                    ),
                  hidden: !allowMealAssigning
                },
                {
                  field: "visitors_mobile_no",
                  title: "Mobile Number",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 130,
                    minWidth: 130,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 130,
                    minWidth: 130,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                },
                {
                  field: "vehicle_no",
                  title: "Vehicle No.",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 105,
                    minWidth: 105,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 105,
                    minWidth: 105,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                },
                {
                  field: "id_proof",
                  title: "ID Proof",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 115,
                    minWidth: 115,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 115,
                    minWidth: 115,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                },
                {
                  field: "visitor_type",
                  title: "Visitor Type",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 115,
                    minWidth: 115,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 115,
                    minWidth: 115,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                  render: (rowData) => rowData?.visitor_type || "-"
                }
              ] : [
                {
                  field: "booking_date",
                  title: "Visit Date",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 125,
                    minWidth: 125,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 125,
                    minWidth: 125,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                },
                {
                  field: "visitors_name",
                  title: "Guest Name",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 125,
                    minWidth: 125,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 125,
                    minWidth: 125,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                },
                {
                  field: "employee_name",
                  title: "Host/Person to Meet",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 145,
                    minWidth: 145,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 145,
                    minWidth: 145,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                },
                {
                  field: "reason",
                  title: "Purpose",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 135,
                    minWidth: 135,
                    padding: "5px 0px 5px 5px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 135,
                    minWidth: 135,
                    textAlign: "start",
                    padding: "5px 0px 5px 7px",
                  },
                },
                {
                  field: "visitors_mobile_no",
                  title: "Guest Mobile Number",
                  editable: "never",
                  headerStyle: {
                    maxWidth: 130,
                    minWidth: 130,
                    padding: "5px 0px 5px 10px",
                    textAlign: "start",
                  },
                  cellStyle: {
                    maxWidth: 130,
                    minWidth: 130,
                    textAlign: "start",
                    padding: "5px 0px 5px 13px",
                  },
                },
              ]
            }
            data={isAll ? formattedVisitorsData : vmPreRegistrationData}
            options={{
              maxBodyHeight: "calc(100vh - 195px)",
              minBodyHeight: "calc(100vh - 195px)",
              rowStyle: {
                fontSize: "13px",
                height: "45px",
              },
              headerStyle: {
                padding: "5px 10px 5px 13px",
                fontWeight: "bold",
              },
              sorting: true,
              search: true,
              searchFieldAlignment: "right",
              searchAutoFocus: false,
              filtering: false,
              paging: true,
              pageSizeOptions: [25, 50],
              pageSize: 25,
              actionsColumnIndex: -1,
              grouping: false,
              columnsButton: false,
              showTitle: true,
              exportFileName: isAll ? "Visitors Report" : "Pre Registration Report",
              exportAllData: true,
              exportButton: true,
              selection: isAll ? true : false,
              selectionProps: (rowData) => ({
                disabled: rowData.checkout_timestamp !== "N/A",
                style: { padding: "0px 5px 0px 5px" },
              }),
              showSelectAllCheckbox: isAll ? true : false,
              showTextRowsSelected: true,
            }}
            onSelectionChange={(selectedRows) => handleSelect(selectedRows)}
            components={{
              Toolbar: (props) => (
                <Box
                  sx={{
                    width: "full",
                    display: "flex",
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: "#f3edfd",
                      padding: "0px 20px",
                      flexGrow: 1,
                    }}
                  >
                    <Typography component="h3" variant="h6">
                      {isAll ? "Visitors Report" : "Pre Registration Report"}
                    </Typography>
                  </Box>
                  <MTableToolbar {...props} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#f3edfd",
                      pr: 2,
                    }}
                  >
                    {selectedRows.length !== 0 && (
                      <Box>
                        <Tooltip title="Check Out All">
                          <IconButton
                            onClick={() => handleCheckOutConfirmation("")}
                          >
                            {visitorLoading ? (
                              <CircularProgress size={24} />
                            ) : (
                              <LogoutIcon color={"primary"} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    <CustomToggle
                      isChecked={!isAll}
                      setIsChecked={handleToggle}
                      handleToggleChange={handleToggle}
                      NoText={"All"}
                      Yestext={"Pre-Register"}
                      style={{
                        height: "32px",
                        padding: "0px",
                        width: "180px",
                        labelPadding: "2px",
                        "&:hover": {
                          backgroundColor: "#f3edfd !important",
                        },
                      }}
                    />
                  </Box>

                </Box>
              ),
              Overlay: () => null,
              OverlayLoading: () => <BackdropMeepl open={true} dark={false} />,
            }}
            title=""
          />
          <CanteenTeamSizeExceededWarningPopup ref={canteenTeamSizeRef} closeReq={true} />
          <ConfirmationDialog
            title={`Check Out`}
            content={
              currentData
                ? `Are you sure you want to check out visitor ${currentData?.visitors_name}?`
                : `Are you sure you want to check out ${selectedRows?.length} visitors?`
            }
            anchorE={anchorE}
            openDialog={openDialog}
            closeDialog={setOpenDialog}
            OkButtonText="Yes"
            onSubmit={currentData ? handleCheckout : handleCheckoutAll}
          />
          <ConfirmationDialog
            title={`Assign Meal Token`}
            content={`Are you sure you want to assign meal token to ${mealData?.visitors_name}?`}
            anchorE={anchorE2}
            openDialog={openDialog2}
            closeDialog={setOpenDialog2}
            OkButtonText="Yes"
            onSubmit={handleMealAssigned}
          />
          <Dialog
            open={transactionDialogOpen}
            onClose={() => setTransactionDialogOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              {transactionVisitor
                ? `In-Out Detail for ${transactionVisitor?.visitors_name}`
                : "In-Out Detail"}
            </DialogTitle>
            <DialogContent dividers>
              {transactionLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : transactionRows?.length > 0 ? (
                <Box>
                  {transactionRows.map((transaction) => (
                    <Box
                      key={transaction?.visitors_management_transactions_pk}
                      sx={{
                        mb: 1.5,
                        px: 2,
                        py: 1,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                      }}
                    >
                      <Typography sx={{ fontWeight: "bold" }}>
                        {transaction?.visitors_name || "Unknown Visitor"}
                      </Typography>
                      <Typography>
                        {transaction?.type ? `${transaction?.type} - ` : ""}
                        {transaction?.checkin_timestamp
                          ? formatTime(transaction?.checkin_timestamp)
                          : transaction?.checkout_timestamp
                          ? formatTime(transaction?.checkout_timestamp)
                          : "No timestamp available"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography>No in-out detail data found for this visitor.</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleExportTransactionsPDF}
                disabled={transactionLoading || !(transactionRows?.length > 0) || pdfGenerating}
                startIcon={pdfGenerating ? <CircularProgress size={18} color="inherit" /> : null}
              >
                Export PDF
              </Button>
              <Button onClick={() => setTransactionDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Base>
  );
};

export default VisitorsRecord;
